import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Facebook, Instagram, MessageCircle, Youtube } from 'lucide-react';
import { contactInfo, socialLinks } from '../data/mock';

const LOGO_URL = "/images/logo.png";

const Footer = () => {
  return (
    <footer className="bg-[#0f1a14] text-white relative overflow-hidden">
      {/* Decorative Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1725710016354-a9da970219c3?w=800&q=60')`,
          backgroundSize: '300px',
          backgroundRepeat: 'repeat'
        }} />
      </div>
      
      {/* Gold Top Border */}
      <div className="h-1 bg-gradient-to-r from-transparent via-[#c5a572] to-transparent" />
      
      {/* Main Footer */}
      <div className="container mx-auto px-4 py-16 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* About Section */}
          <div>
            <div className="flex items-center gap-4 mb-6">
              <img 
                src={LOGO_URL} 
                alt="Fatimia Community Services" 
                className="h-14 w-auto brightness-0 invert"
              />
              <div>
                <h3 className="text-xl text-white">Fatimia Community</h3>
                <p className="text-[#c5a572] text-xs uppercase tracking-[0.2em] font-body">Services Society of BC</p>
              </div>
            </div>
            <p className="text-white/60 text-sm leading-relaxed font-body">
              A Farsi Speaking Afghan Shi'a Muslim Centre serving the community in British Columbia with faith, education, and unity.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-xl text-[#c5a572] mb-6">Quick Links</h4>
            <nav className="flex flex-col gap-3">
              {[
                { name: 'About Us', path: '/about' },
                { name: 'Madrassah', path: '/madrassah' },
                { name: 'Youth Program', path: '/youth-program' },
                { name: 'Membership', path: '/membership' },
                { name: 'Donate', path: '/donate' },
                { name: 'Contact', path: '/contact' }
              ].map((link) => (
                <Link 
                  key={link.name}
                  to={link.path} 
                  className="text-white/60 hover:text-[#c5a572] transition-colors text-sm uppercase tracking-wider"
                >
                  {link.name}
                </Link>
              ))}
            </nav>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-xl text-[#c5a572] mb-6">Contact Us</h4>
            <div className="flex flex-col gap-4">
              <div className="flex items-start gap-4">
                <MapPin className="w-5 h-5 text-[#c5a572] flex-shrink-0 mt-1" />
                <span className="text-white/60 text-sm">{contactInfo.address}</span>
              </div>
              <div className="flex items-center gap-4">
                <Phone className="w-5 h-5 text-[#c5a572] flex-shrink-0" />
                <span className="text-white/60 text-sm">{contactInfo.phone}</span>
              </div>
              <div className="flex items-center gap-4">
                <Mail className="w-5 h-5 text-[#c5a572] flex-shrink-0" />
                <span className="text-white/60 text-sm">{contactInfo.email}</span>
              </div>
            </div>
          </div>

          {/* Connect */}
          <div>
            <h4 className="text-xl text-[#c5a572] mb-6">Connect With Us</h4>
            <div className="flex gap-3 mb-6">
              <a 
                href={socialLinks.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 border border-[#c5a572]/30 hover:border-[#c5a572] hover:bg-[#c5a572]/10 flex items-center justify-center transition-all duration-300"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5 text-[#c5a572]" />
              </a>
              <a 
                href={socialLinks.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 border border-[#c5a572]/30 hover:border-[#c5a572] hover:bg-[#c5a572]/10 flex items-center justify-center transition-all duration-300"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5 text-[#c5a572]" />
              </a>
              <a 
                href={socialLinks.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 border border-[#c5a572]/30 hover:border-[#c5a572] hover:bg-[#c5a572]/10 flex items-center justify-center transition-all duration-300"
                aria-label="WhatsApp"
              >
                <MessageCircle className="w-5 h-5 text-[#c5a572]" />
              </a>
              <a 
                href={socialLinks.youtube}
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 border border-[#c5a572]/30 hover:border-[#c5a572] hover:bg-[#c5a572]/10 flex items-center justify-center transition-all duration-300"
                aria-label="YouTube"
              >
                <Youtube className="w-5 h-5 text-[#c5a572]" />
              </a>
            </div>
            <p className="text-white/60 text-sm font-body">Follow us for updates and live streams.</p>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-[#c5a572]/20">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col lg:flex-row justify-between items-center gap-4">
            <p className="text-white/40 text-sm font-body">
              &copy; {new Date().getFullYear()} Fatimia Community Services Society of BC. All rights reserved.
            </p>
            <p className="text-center lg:text-right">
              <span className="italic text-white/60 text-sm font-body">
                "And hold firmly to the rope of Allah all together and do not become divided."
              </span>
              <span className="text-[#c5a572] ml-2 text-sm font-body">- Quran 3:103</span>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
